import { Link } from "react-router-dom";
import { Anchor, Badge, Group, Paper, Stack, Text } from "@mantine/core";
import {
  IconCalendar,
  IconClock,
  IconFile,
  IconMail,
  IconSpeakerphone,
  IconStar,
  IconTicket,
  IconUser,
} from "@tabler/icons-react";

import { BORDER, SETTINGS_LINK } from "./SetupChecklist";

const SUPPORT_EMAIL = "support@congrs.com";

const GLANCE_ITEMS = [
  { key: "eventDates", label: "Event days", icon: IconCalendar },
  { key: "sessions", label: "Sessions", icon: IconClock },
  { key: "speakers", label: "Speakers", icon: IconUser },
  { key: "sponsors", label: "Sponsors", icon: IconStar },
  { key: "tickets", label: "Ticket holders", icon: IconTicket },
  { key: "pages", label: "Pages", icon: IconFile },
  { key: "announcements", label: "Announcements", icon: IconSpeakerphone },
];

const languageNames = new Intl.DisplayNames(["en"], { type: "language" });

function SidePanelRow({ icon: Icon, label, children }) {
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      py={8}
      style={{ borderTop: BORDER }}
    >
      <Group gap="xs" wrap="nowrap">
        {Icon && <Icon size={16} color="var(--mantine-color-dimmed)" />}
        <Text size="sm">{label}</Text>
      </Group>
      {children}
    </Group>
  );
}

function SidePanelCard({ title, action, children }) {
  return (
    <Paper withBorder radius="md" px="md" pt="sm" pb={4}>
      <Group justify="space-between" mb="xs">
        <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts={1}>
          {title}
        </Text>
        {action}
      </Group>
      {children}
    </Paper>
  );
}

function PanelValue({ value, fallback }) {
  return (
    <Text size="sm" fw={600} c={value ? undefined : "dimmed"}>
      {value || fallback}
    </Text>
  );
}

export default function SetupSidePanel({ event, counts }) {
  const publicPath = `/public-event/${event.slug || event.id}`;

  return (
    <Stack gap="md">
      <SidePanelCard
        title="Event status"
        action={
          <Anchor component={Link} to={SETTINGS_LINK} size="xs" fw={600}>
            Edit
          </Anchor>
        }
      >
        <SidePanelRow label="Status">
          <Badge variant="outline" color="gray" size="sm" radius="sm">
            {event.status}
          </Badge>
        </SidePanelRow>
        <SidePanelRow label="Event days">
          <PanelValue value={counts.eventDates} fallback="Not set" />
        </SidePanelRow>
        <SidePanelRow label="Main language">
          <PanelValue
            value={event.language && languageNames.of(event.language)}
            fallback="Not set"
          />
        </SidePanelRow>
        <SidePanelRow label="Public page">
          {event.isPublic ? (
            <Anchor href={publicPath} target="_blank" size="sm" fw={600}>
              Open
            </Anchor>
          ) : (
            <PanelValue fallback="Off" />
          )}
        </SidePanelRow>
      </SidePanelCard>
      <SidePanelCard title="At a glance">
        {GLANCE_ITEMS.map((item) => (
          <SidePanelRow key={item.key} icon={item.icon} label={item.label}>
            <PanelValue value={Number(counts[item.key])} fallback={0} />
          </SidePanelRow>
        ))}
      </SidePanelCard>
      <SidePanelCard title="Stuck?">
        <SidePanelRow
          icon={IconMail}
          label={
            <Anchor href={`mailto:${SUPPORT_EMAIL}`} size="sm">
              {SUPPORT_EMAIL}
            </Anchor>
          }
        />
      </SidePanelCard>
    </Stack>
  );
}
